﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Link , useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useSignupMutation } from "@/features/organizers/api";
import { ApiError } from "@/lib/api-client";
import { useT } from "@/lib/i18n";

const schema = z.object({
  email: z.string().email("validation.emailInvalid"),
  password: z.string().min(8, "validation.passwordMin"),
  companyName: z.string().min(2, "validation.companyNameMin").max(200),
  phoneNumber: z.string().regex(/^\+[1-9]\d{7,14}$/, "validation.phoneE164"),
});

type SignupFormValues = z.infer<typeof schema>;

export function SignupPage() {
  const t = useT("auth");
  const tCommon = useT("common");
  const navigate = useNavigate();
  const signup = useSignupMutation();

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: "",
      companyName: "",
      phoneNumber: "+90",
    },
  });

  async function onSubmit(values: SignupFormValues) {
    try {
      const result = await signup.mutateAsync(values);
      toast.success(t("signupPage.success", { slug: result.defaultShopSlug }));
      navigate(`/check-email?email=${encodeURIComponent(values.email)}`);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "email_taken") {
          form.setError("email", { message: err.detail });
          return;
        }
        toast.error(err.detail);
      } else {
        toast.error(tCommon("unexpectedError"));
      }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/40">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl">{t("signupPage.title")}</CardTitle>
          <CardDescription>{t("signupPage.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("signupPage.companyName")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("signupPage.companyPlaceholder")} {...field} />
                    </FormControl>
                    <FormDescription>{t("signupPage.companyHint")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("email")}</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder={t("emailPlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("signupPage.phone")}</FormLabel>
                    <FormControl>
                      <Input dir="ltr" placeholder="+905551234567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("password")}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t("signupPage.passwordPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={signup.isPending}
              >
                {signup.isPending ? t("signupPage.submitting") : t("signupPage.submit")}
              </Button>
            </form>
          </Form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t("signupPage.hasAccount")}{" "}
            <Link to="/login" className="font-medium text-foreground hover:underline">
              {t("signupPage.login")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
